// =====================================================================
// Geometría de tracking de campo — PURA y compartida (api + web).
// Sin dependencias: distancia geográfica + chequeo de geocerca (geofence).
// =====================================================================
const EARTH_RADIUS_M = 6_371_000; // radio medio de la Tierra en metros
const toRad = (deg) => (deg * Math.PI) / 180;
/**
 * Distancia en METROS entre dos puntos (fórmula de Haversine). Suficientemente
 * precisa para geocercas urbanas de servicio (errores < 0.5% a estas escalas).
 */
export function haversineMeters(a, b) {
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}
/**
 * Extrae coordenadas de un link de Google Maps (o de "lat,lng" pegado a mano).
 * PURO y sin red. Formatos soportados:
 *   - `...?q=18.48,-69.91` (también `query=`, `ll=`, `destination=`, `q=loc:...`,
 *     con la coma URL-encoded `%2C` incluida)
 *   - `.../@18.4861,-69.9312,17z` (vista de mapa / place)
 *   - `geo:18.48,-69.91`
 *   - `18.48, -69.91` (texto plano)
 *
 * LIMITACIÓN (deliberada): los links ACORTADOS (`maps.app.goo.gl/…`,
 * `goo.gl/maps/…`) no llevan coordenadas en la URL — resolverlos exige seguir
 * la redirección por red, y un fetch server-side de URLs arbitrarias es
 * superficie de SSRF. Devuelven `null`; el link crudo igual se guarda y entra
 * a la ficha de despacho (el técnico lo abre con un tap).
 */
export function parseGoogleMapsLink(raw) {
    if (!raw)
        return null;
    let text = raw.trim();
    if (!text)
        return null;
    try {
        text = decodeURIComponent(text);
    }
    catch {
        // % suelto u encoding inválido: se intenta sobre el texto tal cual.
    }
    const patterns = [
        /[?&](?:q|query|ll|destination)=(?:loc:)?(-?\d{1,2}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)/i,
        /@(-?\d{1,2}(?:\.\d+)?),(-?\d{1,3}(?:\.\d+)?)(?=[,/]|$)/,
        /^geo:(-?\d{1,2}(?:\.\d+)?),(-?\d{1,3}(?:\.\d+)?)/i,
        /^(-?\d{1,2}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)$/,
    ];
    for (const re of patterns) {
        const m = text.match(re);
        if (!m)
            continue;
        const lat = Number(m[1]);
        const lng = Number(m[2]);
        if (Number.isFinite(lat) &&
            Number.isFinite(lng) &&
            Math.abs(lat) <= 90 &&
            Math.abs(lng) <= 180) {
            return { lat, lng };
        }
    }
    return null;
}
/**
 * Chequeo de geocerca: ¿el punto `at` cae dentro de `radiusM` del `target`?
 * Devuelve `null` si falta cualquier dato (sin punto, sin objetivo o sin radio):
 * el llamador muestra "solo ubicación" en ese caso, no dentro/fuera.
 */
export function checkZone(at, target, radiusM) {
    if (!at || !target || radiusM == null || radiusM <= 0)
        return null;
    const distanceM = haversineMeters(at, target);
    return { distanceM, enZona: distanceM <= radiusM };
}
/**
 * Distancia legible para la UI: metros redondeados por debajo de 1 km
 * ("347 m") y kilómetros con un decimal desde ahí ("2.3 km"). PURO.
 */
export function formatDistanceM(distanceM) {
    if (!Number.isFinite(distanceM) || distanceM < 0)
        return '—';
    if (distanceM < 1000)
        return `${Math.round(distanceM)} m`;
    const km = distanceM / 1000;
    // Un decimal, sin ".0" redundante en números redondos (2 km, no 2.0 km).
    const rounded = Math.round(km * 10) / 10;
    return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)} km`;
}
//# sourceMappingURL=geo.js.map